import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { AlertTriangle, FileSpreadsheet, Loader2, Upload } from "lucide-react";
import { G, download, errMsg } from "./guestApi";

const MATCH_TONE = { EXACT: "bg-emerald-100 text-emerald-800", FUZZY: "bg-amber-100 text-amber-900",
  INFERRED: "bg-orange-100 text-orange-900", UNMAPPED: "bg-slate-100 text-slate-600" };

function TableMapping({ t, fields, labels, sel, onChange }) {
  return (
    <div className="border rounded p-2 space-y-2" data-testid={`guest-map-table-${t.table_id}`}>
      <div className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={sel.include}
          onChange={(e) => onChange({ ...sel, include: e.target.checked })}
          data-testid={`guest-map-include-${t.table_id}`} />
        <strong>{t.sheet}</strong> · columns {t.col_start}–{t.col_end}
        {t.header_row ? ` · header row ${t.header_row}` : " · no header row (columns inferred)"}
        · {t.row_count} rows
        {t.needs_confirmation && <Badge className="bg-amber-100 text-amber-900">Confirm mapping</Badge>}
      </div>
      <div className="overflow-x-auto">
        <table className="text-xs">
          <thead><tr>{t.columns.map((c) => (
            <th key={c.col_index} className="p-1 text-left align-top min-w-[150px]">
              <div className="text-slate-500">{c.col_letter} · “{c.header || "no header"}”</div>
              <Select value={sel.columns[String(c.col_index)]}
                onValueChange={(v) => onChange({ ...sel, columns: { ...sel.columns, [String(c.col_index)]: v } })}>
                <SelectTrigger className="h-7 mt-1" data-testid={`guest-map-col-${t.table_id}-${c.col_index}`}>
                  <SelectValue /></SelectTrigger>
                <SelectContent>
                  {[...fields, "ignore"].map((f) => <SelectItem key={f} value={f}>{labels[f]}</SelectItem>)}
                </SelectContent>
              </Select>
              <Badge className={`mt-1 ${MATCH_TONE[c.match]}`}>{c.match}{c.match === "FUZZY" ? ` ${c.score}%` : ""}</Badge>
            </th>))}</tr></thead>
          <tbody>{t.sample_rows.map((r) => (
            <tr key={r.row_no} className="border-t">{t.columns.map((c) => (
              <td key={c.col_index} className="p-1 text-slate-600">{r.cells[String(c.col_index)]}</td>))}</tr>))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function ImportUpload({ session, center, onStaged }) {
  const [file, setFile] = useState(null);
  const [up, setUp] = useState(null);
  const [sel, setSel] = useState({});
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);

  const upload = async () => {
    const fd = new FormData();
    fd.append("token", session.token); fd.append("center", center); fd.append("file", file);
    setBusy(true);
    try {
      const r = await api.post(`${G}/import/upload`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      setUp(r.data); setConfirmed(false);
      setSel(Object.fromEntries(r.data.tables.map((t) => [t.table_id, { table_id: t.table_id, include: true,
        columns: Object.fromEntries(t.columns.map((c) => [String(c.col_index), c.field])) }])));
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const needsConfirm = up?.tables.some((t) => t.needs_confirmation && sel[t.table_id]?.include);
  const applyMapping = async () => {
    setBusy(true);
    try {
      const r = await api.post(`${G}/import/mapping`, { token: session.token, batch_id: up.batch_id,
        tables: Object.values(sel) });
      onStaged(r.data.batch_id); setUp(null); setFile(null);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const fields = up ? Object.keys(up.field_labels).filter((f) => f !== "ignore") : [];
  return (
    <Card data-testid="guest-import-upload">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-sm flex items-center gap-2"><FileSpreadsheet className="w-4 h-4" />Import Excel / CSV · {center}</CardTitle>
        <Button size="sm" variant="outline" data-testid="guest-template-btn"
          onClick={() => download(`${G}/import/template`, { token: session.token }, "guest_contacts_template.xlsx")}>
          Download template</Button>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-2">
          <input type="file" accept=".xlsx,.xls,.csv" onChange={(e) => { setFile(e.target.files[0]); setUp(null); }}
            data-testid="guest-import-file" className="text-sm" />
          <Button size="sm" onClick={upload} disabled={!file || busy} data-testid="guest-import-upload-btn">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}Read file</Button>
        </div>
        <p className="text-[11px] text-slate-500">Nothing is saved until you review and confirm. Importing never sends WhatsApp messages.</p>
        {up && (
          <div className="space-y-3" data-testid="guest-import-mapping">
            {up.previously_imported && (
              <div className="rounded border border-amber-300 bg-amber-50 p-2 text-xs flex gap-2" data-testid="guest-import-reupload-warning">
                <AlertTriangle className="w-4 h-4" /> This exact file was already imported on{" "}
                {(up.previously_imported.committed_at || "").slice(0, 10)}. Existing guests will be matched, not duplicated.
              </div>)}
            <div className="text-sm">Found <strong>{up.tables.length}</strong> table(s), {up.total_rows} rows. Check each column mapping:</div>
            {up.tables.map((t) => (
              <TableMapping key={t.table_id} t={t} fields={fields} labels={up.field_labels}
                sel={sel[t.table_id]} onChange={(v) => setSel({ ...sel, [t.table_id]: v })} />))}
            {needsConfirm && (
              <label className="flex items-center gap-2 text-sm" data-testid="guest-map-confirm-label">
                <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)}
                  data-testid="guest-map-confirm" />
                I have checked the guessed (FUZZY / INFERRED) column mappings above.
              </label>)}
            <Button onClick={applyMapping} disabled={busy || (needsConfirm && !confirmed)}
              data-testid="guest-map-apply">Preview rows</Button>
          </div>)}
      </CardContent>
    </Card>
  );
}
